<template>
  <div>
    <div v-if="surveyEvent && surveyIsOpen && surveyBelongsToCurrentParticipant">
      <h3>{{ surveyEvent.name }}</h3>

      <div v-if="submitError" class="alert alert-danger" data-submit-error>
        <button type="button" class="close" @click="submitError = false">
          <span>&times;</span>
        </button>
        {{ submitError }}
      </div>

      <div v-if="partialDataLoaded" class="alert alert-info">
        <button type="button" class="close" @click="partialDataLoaded = false">
          <span>&times;</span>
        </button>
        {{ $t('survey.resumeSavedProgress') }}
      </div>

      <survey v-show="!isSubmitting && !submitError" :survey="surveyJsModel" />
      <b-spinner v-if="isSubmitting" />

      <router-link v-if="showRedirect" :to="redirectTo" class="btn btn-primary">
        {{ dashboardSurveys.length > 0 ? $t('portal.nextSurvey') : $t('portal.returnToDashboard') }}
      </router-link>
    </div>
    <div v-else>
      <div class="row">
        <div class="col-md-12 text-center">
          <div>
            <h1>{{ $t('portal.sorryAboutThat') }}</h1>
            <div>
              {{ eventStatus }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

  import * as SurveyJS from 'survey-vue';
  import * as widgets from 'surveyjs-widgets';
  import * as nouislider from 'nouislider';
  import { BSpinner } from 'bootstrap-vue';
  import { debounce } from 'lodash-es';
  import store from '../store';

  widgets.nouislider(SurveyJS);
  SurveyJS.StylesManager.applyTheme('bootstrap');
  SurveyJS.defaultBootstrapCss.navigationButton = "btn";
  SurveyJS.defaultBootstrapCss.navigation.prev = "btn-default";
  SurveyJS.defaultBootstrapCss.navigation.next = "btn-default";
  SurveyJS.defaultBootstrapCss.navigation.complete = "btn-primary";

  export default {
    name: "Survey",
    components: {
      survey: SurveyJS.Survey,
      BSpinner,
    },
    beforeRouteEnter: async (to, from, next) => {
      let apiId = to.params.api_id;
      try {
        let surveyEvent = await store.dispatch('GET_SURVEY_EVENT', apiId);
        // Allows us to pass data to the component instance
        next(vm => {
          vm.initData(surveyEvent, from);
        });
      } catch (err) {
        // 400+ errors will go here so we can display a not found message
        next();
      }
    },
    beforeRouteUpdate: async function (to, from, next) {
      let apiId = to.params.api_id;
      try {
        let surveyEvent = await store.dispatch('GET_SURVEY_EVENT', apiId);
        this.initData(surveyEvent);
        next();
      } catch (err) {
        // debugger;
        // 400+ errors will go here so we can display a not found message
        next();
      }
    },
    data() {
      return {
        isSubmitting: false,
        surveyEvent: null,
        surveyJsModel: null,
        submitError: false,
        eventError: false,
        redirectPath: null,
        partialDataLoaded: false,
      };
    },
    computed: {
      participant() {
        return this.$store.getters.getParticipant;
      },
      dashboardSurveys() {
        return this.$store.state.dashboardSurveys;
      },
      surveyBelongsToCurrentParticipant() {
        if (!this.participant || this.surveyEvent.participant_id == this.participant.id) {
          return true;
        }
        return false;
      },
      surveyIsOpen() {
        return this.surveyEvent.status === 'open';
      },
      eventStatus() {
        const status = this.surveyEvent ? this.surveyEvent.status : 'notfound';
        switch (status) {
          case 'completed':
            return this.$t('survey.alreadyCompleted');
          case 'before':
            return this.$t('survey.notYetOpen');
          case 'after':
            return this.$t('survey.surveyClosed');
          default:
            return this.$t('survey.notFound');
        }
      },
      showRedirect() {
        return this.participant && this.surveyJsModel.isCompleted && !this.isSubmitting;
      },
      redirectTo() {
        let to = {name: 'dashboard'};
        if (this.redirectPath) {
          to.params = {path: this.redirectPath};
        }
        return to;
      }
    },
    methods: {
      initData(surveyEvent, from) {
        // reset data in case this is coming from beforeRouteUpdate
        Object.assign(this.$data, this.$options.data.call(this));

        this.surveyEvent = surveyEvent;

        this.surveyJsModel = new SurveyJS.Model(surveyEvent.survey_definition);
        this.surveyJsModel.locale = this.$i18n.locale;

        // replace any variable placeholders with variables passed in from the tranformer
        Object.keys(surveyEvent.variables).forEach((key) => {
          this.surveyJsModel.setVariable(key, surveyEvent.variables[key]);
        });

        if (this.surveyEvent.partial_data) {
          if (this.surveyEvent.partial_data.data) {
            this.surveyJsModel.data = {...this.surveyEvent.partial_data.data}; // make copy

            // SurveyJS caches anything you set in data, which is not ideal
            // for calculated values.  Unset those so they get recalculated.
            this.surveyJsModel.calculatedValues.forEach((val) => {
              this.surveyJsModel.deleteDataValueCore(this.surveyJsModel.valuesHash, val.propertyHash.name);
            });
          }
          if (this.surveyEvent.partial_data.page) {
            this.surveyJsModel.currentPageNo = this.surveyEvent.partial_data.page;
          }
          this.partialDataLoaded = true;
        } else {
          // We want to force the first page when this is a brand new survey, we don't want to skip intro pages without questions
          this.surveyJsModel.currentPageNo = 0;
        }

        // scroll to top when changing pages
        this.surveyJsModel
          .onCurrentPageChanged
          .add(function () {
            setTimeout(function () {
              window.scroll(0, 0);
            }, 10);
          });

        // don't set up the watchers until after we've initialized (and possibly loaded) the surveyJsModel
        this.$watch('surveyJsModel.data', debounce(this.updatePartialData, 500), {deep: true});
        this.$watch('surveyJsModel.currentPageNo', debounce(this.updatePartialData, 500));

        this.surveyJsModel.onComplete.add(this.submitForm);

        // store the page we came from so we can redirect back
        if (from.params && from.params.path) {
          this.redirectPath = from.params.path;
        }
      },
      async updatePartialData() {
        try {
          await this.$store.dispatch('SAVE_PARTIAL_SURVEY', {
            api_id: this.surveyEvent.api_id,
            data: {
              page: this.surveyJsModel.currentPageNo,
              data: this.surveyJsModel.data
            }
          });
        } catch (err) {
          // what do we do if we get an error here?
        }
      },
      async submitForm(sender, options) {
        options.showSaveInProgress();
        this.isSubmitting = true;

        try {
          await this.$store.dispatch('SUBMIT_SURVEY', {
            api_id: this.surveyEvent.api_id,
            body: this.surveyJsModel.data,
          });

          options.showSaveSuccess();
          // check for new dashboard surveys
          await this.$store.dispatch('LOAD_REDIRECT_SURVEYS');

          this.isSubmitting = false;

          // redirect to old portal if we were passed in a redirect url
          if (window) {
            // eslint-disable-next-line compat/compat
            const params = new URLSearchParams(window.location.search);
            const redirect = params.get('redirect');
            if (redirect) {
              window.location = redirect;
            }
          }
        } catch (err) {
          this.isSubmitting = false;
          if (err.status == 403 && err.message) {
            this.submitError = err.message;
          } else {
            this.submitError = this.$t('survey.refreshPage');
          }
          options.showSaveError();
        }
      }
    },
  };

</script>

<style>
label > small {
  font-weight: normal;
  font-style: italic;
}

.radio > label > input {
  opacity: 1 !important;
  z-index: auto;
}

.sv_container .panel-footer {
  background: none;
  border-top: none;
}
</style>
